<template>
  <div :class="filtered ? 'container-fluid product-wrapper sidebaron' : 'container-fluid product-wrapper'">
    <div class="product-grid">
      
      <div class="feature-products">
        <BaseFilter
          screenKey="parcel-list-page"
          :model-value="allParams"
          @update:model-value="val => Object.assign(allParams, val)"
          :fields="filterConfig"
          @search="handleSearch"
          @reset="handleReset"
        />
      </div>
      
      <div class="p-4 bg-white mt-3 shadow-sm rounded">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h3 class="m-0">Parcels List</h3>
          <div class="table-tools d-flex">
            <button v-if="selectedIds.length > 0" class="btn btn-sm btn-success me-2" @click="onExport">
              <i class="fa fa-download me-1"></i> Export
            </button>
            <button v-if="selectedIds.length > 0" class="btn btn-sm btn-outline-success me-2" @click="onExportManifest">
              <i class="fa fa-download me-1"></i> Export Manifest
            </button>
            <button class="btn btn-sm btn-outline-secondary me-2" @click="fetchData">
              <i class="fa fa-refresh me-1"></i> Refresh
            </button>
            <button class="btn btn-sm btn-primary" @click="toggleForm">
              <i class="fa fa-plus"></i> Add
            </button>
          </div>
        </div>

        <BaseTable
          storage-key="parcels"
          :columns="tableCols"
          :data="tableData"
          :loading="loading"
          :total-items="total"
          v-model:current-page="allParams.page"
          v-model:page-size="allParams.limit"
          show-checkbox
          @sort="handleSort"
          @selection-change="handleSelection"
        >
          <template #cell(status)="{ item }">
            <span :class="item.status === 'Active' ? 'badge bg-success' : 'badge bg-secondary'">
              {{ item.status }}
            </span>
          </template>
          <template #cell(created_at)="{ item }">
            <div class="d-flex flex-column py-1 line-height-base">
              <span>Departed: {{ formatDateTime(item.departed_date) }}</span>
              <span>Delivered: {{ formatDateTime(item.delivered_date) }}</span>
            </div>
          </template>
          <template #cell(manifest_id)="{ item }">
            <div class="d-flex flex-column py-1 line-height-base">
              <span>{{ item.manifest?.name || '' }}</span>
            </div>
          </template>
          <template #cell(fee)="{ item }">
            <div class="d-flex flex-column py-1 line-height-base">
              {{ Number(item.fee).toLocaleString() }}
            </div>
          </template>
          <template #cell(total_order)="{ item }">
            <div class="d-flex flex-column py-1 line-height-base">
              <span>{{ item.orders?.length || 0 }}</span>
            </div>
          </template>
          <template #actions="{ item }">
            <button class="btn btn-sm btn-info me-1 text-white" @click="viewOrders(item)" title="View Orders">
              <i class="fa fa-eye"></i>
            </button>
            <button class="btn btn-sm btn-primary me-1" @click="editUser(item)">
              <i class="fa fa-edit"></i>
            </button>
            <button class="btn btn-sm btn-danger" @click="deleteData(item)">
              <i class="fa fa-trash"></i>
            </button>
          </template>
        </BaseTable>
      </div>
    </div>
    <Form 
      :isOpen="isShowForm" 
      :itemData="selectedItem"  @close="isShowForm = false" 
      @save="handleSaveData"
    />

    <!-- Modal View Orders in Parcel -->
    <modalOrders 
      :isOpen="isShowOrders" 
      :parcel="selectedItem" 
      :orders="selectedOrders" 
      @close="isShowOrders = false" 
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';

import BaseFilter from "@/components/common/BaseFilter.vue";
import BaseTable from "@/components/common/BaseTable.vue";
import { useParcels } from "@/composables/useParcels";
import Form from './Form.vue'
import modalOrders from './modalOrders.vue'
import { createToaster } from "@meforma/vue-toaster";

const toaster = createToaster({
  position: "top-right",
  duration: 2000,
});
const { fetchParcelsApi, createParcelApi, updateParcelApi, deleteParcelApi, exportParcelsApi, exportManifestApi } = useParcels();
const filtered = ref(false);
const loading = ref(false);
const tableData = ref([]);
const total = ref(0);
const isShowForm = ref(false)
const selectedItem = ref(null);
const selectedIds = ref([]);

const isShowOrders = ref(false);
const selectedOrders = ref([]);

const allParams = reactive({
  page: 1,
  limit: 10,
  orderBy: 'id',
  sortedBy: 'desc',
  order: '',
  searchJoin: 'AND',
  include: 'manifest,orders',
  searchFields: '',
  search: '',
});

// Cấu hình các ô nhập của Filter
const filterConfig = [
  {
    key: 'name',
    type: 'text',
    label: 'Name',
    operator: 'like',
    placeholder: 'Name...',
    isDefault: true
  },
  {
    key: 'code',
    type: 'text',
    operator: 'like',
    label: 'Code',
    placeholder: 'Code',
    isDefault: true
  },
  {
    key: 'tracking_number',
    type: 'text',
    operator: 'like',
    label: 'Tracking Number',
    placeholder: 'Tracking Number',
    isDefault: true
  },
];

// Cấu hình các cột của Bảng
const tableCols = [
  { key: 'id', label: 'ID', width: '70px', sortable: true },
  { key: 'name', label: 'Name', sortable: true },
  { key: 'code', label: 'Code', sortable: true },
  { key: 'tracking_number', label: 'Tracking Number' },
  { key: 'status', label: 'Status', align: 'center' },
  { key: 'total_order', label: 'Total Order', align: 'center' },
  { key: 'fee', label: 'Fee', align: 'center' },
  { key: 'manifest_id', label: 'Manifest', align: 'center' },
  { key: 'weight', label: 'Weight', align: 'center' },
  { key: 'created_at', label: 'Trace Date' },
];

const fetchData = async (resetPage = false) => {
  if (resetPage) {
    allParams.page = 1;
  }

  loading.value = true;

  try {
    const res = await fetchParcelsApi({ ...allParams });

    if (res.success && res.data) {
      tableData.value = res.data.data || [];

      total.value = res.data.meta?.pagination?.total || 0;
    } else {
      toaster.error(res.error || "Không thể tải dữ liệu");
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra trong quá trình kết nối");
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  if (allParams.page === 1) fetchData();
  else allParams.page = 1;
};

const handleReset = () => {
  allParams.search = '';
  allParams.searchFields = '';
  
  if (allParams.page === 1) fetchData();
  else allParams.page = 1;
};

const handleSort = ({ key, order }) => {
  allParams.orderBy = key;
  allParams.order = order;
  fetchData();
};

const handleSelection = (ids) => {
  selectedIds.value = ids;
};

const onExport = async () => {
  if (selectedIds.value.length === 0) return;
  loading.value = true;
  try {
    const blob = await exportParcelsApi({ ids: selectedIds.value });
    if (blob instanceof Blob && blob.type === 'application/json') {
      const text = await blob.text();
      const result = JSON.parse(text);
      toaster.error(result.message || 'Export thất bại');
      return;
    }
    if (blob && !blob.error) {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'export_parcels.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toaster.success('Export thành công!');
    } else {
      toaster.error(blob.message || 'Export thất bại');
    }
  } catch (err) {
    toaster.error('Có lỗi xảy ra khi export file');
  } finally {
    loading.value = false;
  }
};

const onExportManifest = async () => {
  if (selectedIds.value.length === 0) return;
  loading.value = true;
  try {
    const blob = await exportManifestApi({ ids: selectedIds.value });
    if (blob instanceof Blob && blob.type === 'application/json') {
      const text = await blob.text();
      const result = JSON.parse(text);
      toaster.error(result.message || 'Export manifest thất bại');
      return;
    }
    if (blob && !blob.error) {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'export_manifest.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toaster.success('Export manifest thành công!');
    } else {
      toaster.error(blob.message || 'Export manifest thất bại');
    }
  } catch (err) {
    toaster.error('Có lỗi xảy ra khi export manifest');
  } finally {
    loading.value = false;
  }
};

watch(() => [allParams.page, allParams.limit], () => fetchData());

onMounted(fetchData);

const toggleForm = () => {
  selectedItem.value = null;
  isShowForm.value = true;
};

const handleSaveData = async (data) => {
  try {
    let res;
    if (data.id) {
      res = await updateParcelApi(data.id, data);
    } else {
      res = await createParcelApi(data);
    }

    if (res.success) {
      toaster.success(data.id ? "Cập nhật thành công!" : "Thêm mới thành công!");
      isShowForm.value = false; 
      fetchData();
    } else {
      toaster.error(res.error || "Thao tác thất bại");
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra trong quá trình kết nối");
  }
};

const editUser = (user) => {
  selectedItem.value = { ...user };
  isShowForm.value = true;
};

const viewOrders = (item) => {
  selectedItem.value = item;
  selectedOrders.value = item.orders || [];
  isShowOrders.value = true;
};

const deleteData = async (item) => {
  if (confirm('Xóa ID ' + item.id + '?')) {
    try {
      const res = await deleteParcelApi(item.id);
      if (res.success) {
        toaster.success("Xóa thành công!");
        fetchData(); // Tải lại danh sách
      } else {
        toaster.error(res.error || "Xóa thất bại");
      }
    } catch (err) {
      toaster.error("Có lỗi xảy ra trong quá trình kết nối");
    }
  }
};
const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);

  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();

  const h = String(date.getHours()).padStart(2, '0');
  const i = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');

  return `${d}/${m}/${y} ${h}:${i}:${s}`;
};
</script>

<style scoped>
.filter-section .left-filter {
  max-height: 400px;
  overflow-y: auto;
  z-index: 99 !important;
}
.product-wrapper {
  padding-bottom: 50px;
}

.product-wrapper {
  padding-bottom: 50px;
}
</style>